import React, { useCallback, useRef, useState } from "react";

import { faCheck } from "@fortawesome/pro-solid-svg-icons/faCheck";
import { faPen } from "@fortawesome/pro-solid-svg-icons/faPen";
import { faTimes } from "@fortawesome/pro-solid-svg-icons/faTimes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import { useIntl } from "react-intl";

import { Deal } from "@mapmycustomers/shared/types/entity";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button, Labeled, NumberField, NumberFieldComponent } from "@mapmycustomers/ui";

import { dealAnalytics } from "@app/component/preview/analytics";
import localSettings from "@app/config/LocalSettings";

import styles from "./AmountField.module.scss";
import commonStyles from "./CommonField.module.scss";

interface Props {
  disabled?: boolean;
  onChange?: (value: Deal["amount"]) => void;
  value: Deal["amount"];
}

const AmountField: React.FC<Props> = ({ disabled, onChange, value }) => {
  const intl = useIntl();

  const [amount, setAmount] = useState<number | undefined>(value == null ? undefined : value);

  const [editing, startEditing, cancelEditing] = useBoolean();

  const handleSave = useCallback(() => {
    cancelEditing();
    onChange?.(amount ?? null);
  }, [amount, cancelEditing, onChange]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        cancelEditing();
      } else if (event.key === "Enter") {
        event.stopPropagation();
        handleSave();
      }
    },
    [cancelEditing, handleSave]
  );

  const inputRef = useRef<null | NumberFieldComponent>(null);
  const handleStartEditing = useCallback(() => {
    if (disabled) {
      return;
    }
    dealAnalytics.clicked(["Edited Deal Amount"]);
    setAmount(value == null ? undefined : value);
    startEditing();
    // can't focus hidden field, need to wait until it will be displayed
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }, [disabled, setAmount, startEditing, value]);

  return (
    <Labeled
      className={commonStyles.container}
      label={
        <span>
          {intl.formatMessage({
            id: "dealRecord.quickEdit.dealAmount",
            defaultMessage: "Amount",
            description: "Title of Deal amount details of a deal",
          })}
          {!disabled && (
            <FontAwesomeIcon
              className={commonStyles.editIcon}
              icon={faPen}
              onClick={handleStartEditing}
              size="xs"
            />
          )}
        </span>
      }
    >
      <div className={cn(styles.inputContainer, { [commonStyles.hidden]: !editing })}>
        <NumberField
          disabled={disabled}
          inputClassName={styles.inputField}
          min={0}
          onChange={setAmount}
          onKeyDown={handleKeyDown}
          precision={2}
          ref={inputRef}
          size="middle"
          value={amount}
        />
        {!disabled && (
          <>
            <Button
              icon={<FontAwesomeIcon icon={faCheck} />}
              iconOnly
              onClick={handleSave}
              type="text"
            />
            <Button
              icon={<FontAwesomeIcon icon={faTimes} />}
              iconOnly
              onClick={cancelEditing}
              type="text"
            />
          </>
        )}
      </div>
      <div
        className={cn(styles.formattedValue, {
          [commonStyles.hidden]: editing,
          [styles.disabled]: disabled,
        })}
        onClick={handleStartEditing}
      >
        {value != null
          ? intl.formatNumber(value, {
              currency: localSettings.getCurrencyCode(),
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
              style: "currency",
            })
          : "–"}
      </div>
    </Labeled>
  );
};

export default AmountField;
